import React from 'react';
import { Switch } from 'antd';
import styled from 'styled-components';
import moment from 'moment';
import { EyeOutlined } from '@ant-design/icons';
import { ButtonWrapper } from 'components';
import { Text } from 'atoms';

// Constants
export const DATE_FORMAT = 'DD-MM-YYYY';
export const DATE_TIME_FORMAT = 'DD-MM-YYYY, HH:mm';

// Filter Options
export const StatusOptions = [
	{ value: true, label: 'Active' },
	{ value: false, label: 'Inactive' },
];

export const ApprovalStatusOptions = [
	{ value: 'pending', label: 'Pending' },
	{ value: 'approved', label: 'Approved' },
	{ value: 'rejected', label: 'Rejected' },
];

// Format a numeric amount as INR; '-' when missing/invalid.
const formatINR = (value) => {
	const n = Number(value);
	if (value == null || Number.isNaN(n)) return '-';
	return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Styled Components
const ViewButton = styled(ButtonWrapper)`
	padding: 4px;
	min-width: 32px;
	min-height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
`;

// Table Columns Configuration
export const getTableColumns = (navigate, handleStatusToggle, statusUpdateLoading = {}) => [
	{
		title: 'Client Name',
		dataIndex: 'client_name',
		key: 'client_name',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Project Code',
		dataIndex: 'project_code',
		key: 'project_code',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Route',
		dataIndex: 'route_name',
		key: 'route_name',
		render: (text, record) => (
			<Text>
				{text || '-'} {record.route_code ? `(${record.route_code})` : ''}
			</Text>
		),
	},
	{
		title: 'Billing Type',
		dataIndex: 'billing_type',
		key: 'billing_type',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Sub Billing Type',
		dataIndex: 'billing_subtype',
		key: 'billing_subtype',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Vehicle Type',
		dataIndex: 'vehicle_type_name',
		key: 'vehicle_type_name',
		render: (text) => <Text>{text || '-'}</Text>,
	},
	{
		title: 'Fixed Vehicle Rate',
		dataIndex: 'fixed_vehicle_rate',
		key: 'fixed_vehicle_rate',
		render: (value) => <Text>{formatINR(value)}</Text>,
	},
	{
		title: 'Approval Status',
		dataIndex: 'approval_status',
		key: 'approval_status',
		render: (status, record) => {
			if (record?.has_pending_edit) {
				return (
					<Text style={{ color: '#faad14', fontWeight: 500 }}>Pending Edit Approval</Text>
				);
			}
			const statusColors = {
				pending: '#faad14',
				approved: '#52c41a',
				rejected: '#ff4d4f',
			};
			return (
				<Text style={{ color: statusColors[status] || '#000', fontWeight: 500 }}>
					{status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}
				</Text>
			);
		},
	},
	{
		title: 'Created At',
		dataIndex: 'created_at',
		key: 'created_at',
		render: (text) => {
			if (!text) return <Text>-</Text>;
			try {
				const time = moment(text);

				if (!time.isValid()) return <Text>-</Text>;

				return (
					<div>
						<Text style={{ display: 'block' }}>{time.format('DD-MM-YYYY')}</Text>
						<Text style={{ display: 'block' }}>{time.format('h:mm a')}</Text>
					</div>
				);
			} catch (error) {
				return <Text>-</Text>;
			}
		},
	},
	{
		title: 'Approved By',
		dataIndex: 'approver_name',
		key: 'approver_name',
		render: (text, record) => {
			if (!text) return <Text>-</Text>;
			let approvedDate = '';
			if (record.approved_at) {
				// Convert UTC to IST (UTC+5:30)
				const utcTime = moment.utc(record.approved_at);
				if (utcTime.isValid()) {
					const istTime = utcTime.clone().add(330, 'minutes'); // Add 5 hours 30 minutes
					approvedDate = istTime.format(DATE_FORMAT);
				}
			}
			return (
				<div>
					<Text style={{ fontSize: '12px', display: 'block' }}>{text}</Text>
					{approvedDate && (
						<Text style={{ fontSize: '11px', color: '#888' }}>{approvedDate}</Text>
					)}
				</div>
			);
		},
	},
	{
		title: 'Status',
		dataIndex: 'is_active',
		key: 'is_active',
		render: (is_active, record) => (
			<Switch
				checked={is_active}
				checkedChildren="Active"
				unCheckedChildren="Inactive"
				onChange={() => handleStatusToggle?.(record)}
				disabled={statusUpdateLoading?.[record.id] || record?.has_pending_edit}
			/>
		),
	},
	{
		title: 'View',
		dataIndex: 'id',
		key: 'view',
		render: (id, record) => (
			<ViewButton
				onClick={() =>
					navigate(`/client-contracts/${id}`, { state: { clientId: record?.client_id } })
				}
			>
				<EyeOutlined style={{ fontSize: 18, color: '#1890ff' }} />
			</ViewButton>
		),
	},
];
